import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { duration, enter, exit, stagger } from '@/lib/motion'
import { Button } from '@/components/site/button'
import { RollText } from '@/components/site/roll-text'

import {
    legalLinks,
    primaryLinks,
    siteConfig
} from '@/constants/site'

interface MenuToggleProps {
    open: boolean
    onToggle: () => void
}

const toggleEase = 'ease-studio'

const MenuToggle: FC<MenuToggleProps> = ({ open, onToggle }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onToggle}
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
            aria-controls='site-menu'
            className='relative size-9 shrink-0 cursor-pointer'
        >
            <span aria-hidden='true' className='absolute inset-0 flex flex-col items-center justify-center gap-1'>
                {[0, 1].map((index) => (
                    <span key={index} className='relative h-0.5 w-[28.8px] overflow-hidden'>
                        <span
                            className={cn('absolute inset-0 origin-left bg-foreground transition-transform duration-fast', toggleEase)}
                            style={{
                                transform: open ? 'scaleX(0)' : 'scaleX(1)',
                                transitionDelay: `${open ? index * 0.05 : 0.15 + index * 0.05}s`
                            }}
                        />
                    </span>
                ))}
            </span>

            <span aria-hidden='true' className='absolute inset-0'>
                {[-45, 45].map((angle, index) => (
                    <span
                        key={angle}
                        className='absolute left-[calc(50%-14.544px)] top-[calc(50%-1px)] h-0.5 w-[29.088px] overflow-hidden'
                        style={{ transform: `rotate(${angle}deg)` }}
                    >
                        <span
                            className={cn('absolute inset-0 origin-right bg-foreground transition-transform duration-fast', toggleEase)}
                            style={{
                                transform: open ? 'scaleX(1)' : 'scaleX(0)',
                                transitionDelay: `${open ? 0.15 + index * 0.05 : index * 0.05}s`
                            }}
                        />
                    </span>
                ))}
            </span>
        </button>
    )
}

export const Navbar: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const navRef = useRef<HTMLElement>(null)
    const router = useRouter()

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const handlePointer = (event: PointerEvent) => {
            if (navRef.current && !navRef.current.contains(event.target as Node)) setOpen(false)
        }

        document.addEventListener('keydown', handleKey)
        document.addEventListener('pointerdown', handlePointer)

        return () => {
            document.removeEventListener('keydown', handleKey)
            document.removeEventListener('pointerdown', handlePointer)
        }
    }, [open])

    const isCurrent = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header className='pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-3'>
            <motion.nav
                ref={navRef}
                aria-label="Main"
                initial={{ y: -12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={enter(0.1)}
                className='pointer-events-auto flex w-full max-w-[350px] flex-col overflow-clip bg-coal px-4 py-[10px] shadow-overlay ring-1 ring-white/[0.07] md:max-w-[424px]'
            >
                <div className='flex w-full items-center justify-between'>
                    <Link
                        href='/'
                        aria-label={`${siteConfig.name}, home`}
                        className='t-logo select-none text-foreground'
                    >
                        {siteConfig.name}
                    </Link>

                    <div className='flex items-center gap-5'>
                        <Button href='/contact' label="Say hello" size='sm' />
                        <MenuToggle open={open} onToggle={() => setOpen((value) => !value)} />
                    </div>
                </div>

                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            key='site-menu'
                            id='site-menu'
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0, transition: enter(0, duration.base) }}
                            exit={{ opacity: 0, transition: exit() }}
                        >
                            <div className='flex w-full flex-col items-center gap-6 px-2 pb-3 pt-10'>
                                <ul className='flex w-full flex-col items-center'>
                                    {primaryLinks.map((link, index) => (
                                        <motion.li
                                            key={link.href}
                                            className='w-full'
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0, transition: enter(0.05 + stagger(index, 0.04), duration.base) }}
                                            exit={{ opacity: 0, transition: exit() }}
                                        >
                                            <Link
                                                href={link.href}
                                                aria-current={isCurrent(link.href) ? 'page' : undefined}
                                                className='roll-trigger t-menu block w-full py-1 text-center text-foreground'
                                            >
                                                <RollText text={link.label} />
                                            </Link>
                                        </motion.li>
                                    ))}
                                </ul>

                                <motion.p
                                    className='t-link flex flex-wrap items-center justify-center gap-[2px] text-foreground'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1, transition: enter(0.2, duration.base) }}
                                    exit={{ opacity: 0, transition: exit() }}
                                >
                                    {legalLinks.map((link, index) => (
                                        <Fragment key={link.href}>
                                            <Link href={link.href} className='roll-trigger px-1'>
                                                <RollText text={link.label} />
                                            </Link>

                                            {index < legalLinks.length - 1 && (
                                                <span aria-hidden='true' className='text-muted-foreground'>
                                                    /
                                                </span>
                                            )}
                                        </Fragment>
                                    ))}
                                </motion.p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.nav>
        </header>
    )
}
